import axios from 'axios';

// Determine API URL with fallback logic
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Fallback logic for different environments
  if (window.location.hostname === 'localhost') {
    return 'http://localhost:7123';
  }
  
  // For production, always use AWS API Gateway
  return 'https://dqpqit1741.execute-api.us-west-2.amazonaws.com/prod';
};

const API_URL = getApiUrl();

console.log('API configured with base URL:', API_URL);

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  timeout: 60000, // 60 second timeout (1 minute)
});

// Add request interceptor for debugging + impersonation header
axiosInstance.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
    // Inject impersonation headers if active
    const impersonatingUser = localStorage.getItem('stackbd_impersonating_user');
    if (impersonatingUser) {
      config.headers['x-impersonate-user'] = impersonatingUser;
      const impersonatingEmail = localStorage.getItem('stackbd_impersonating_email');
      if (impersonatingEmail) {
        config.headers['x-impersonate-email'] = impersonatingEmail;
      }
    }
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging
axiosInstance.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.status, error.config?.url, error.message);
    return Promise.reject(error);
  }
);

export const checkAuthStatus = async () => {
  try {
    // Try to get a resource that requires authentication
    await axiosInstance.get('/v1/auth-status');
    return true;
  } catch (error) {
    if (error.response?.status === 401) {
      return false;
    }
    // For other errors, assume we're authenticated but there's another issue
    return true;
  }
};

export class APIError extends Error {
  constructor(message, status, data) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

// HubSpot API functions
export const hubspotAPI = {
  getConnectionStatus: async (authorizationToken, tenant) => {
    try {
      const endpoint = '/v1/hubspot/status';
  
      const response = await axiosInstance.get(endpoint, {
        headers: {
          'Authorization': 'Bearer ' + authorizationToken,
          'tenant': tenant
        }
      });
      
      console.log('HubSpot connection status response:', response.data);
      
      // Check if the status endpoint returned token_valid field
      // If not, assume token is valid if 'connected' is true
      const result = {
        ...response.data,
        token_valid: response.data.hasOwnProperty('token_valid') 
          ? response.data.token_valid 
          : response.data.connected
      };
      
      return result;
    } catch (error) {
      console.error('Error checking HubSpot connection:', error);
      return { connected: false, token_valid: false, error: error.message };
    }
  },

  connect: async (userId) => {
    try {
      console.log('Initiating HubSpot connection for user:', userId);
      
      // Generate a random state value for security
      const stateValue = Array(32).fill(0).map(() => Math.random().toString(36).charAt(2)).join('');
      console.log('Generated state value:', stateValue);
      
      // Store the state in localStorage for verification in callback
      localStorage.setItem('hubspot_oauth_state', stateValue);
      
      // If we have a real user ID, store that too
      if (userId) {
        localStorage.setItem('real_user_id', userId);
      }
      
      // Store the current page path to return to
      localStorage.setItem('returnToUrl', window.location.pathname);
      
      // Initialize parameters for the request
      let params = { state: stateValue };
      if (userId) {
        params.user_id = userId;
      }
      
      console.log('Requesting OAuth URL with params:', params);
      const response = await axiosInstance.get('/v1/hubspot/oauth-url', { params });
      
      if (response.data?.authUrl) {
        console.log('Received auth URL:', response.data.authUrl);
        
        // Navigate to auth URL
        window.location.href = response.data.authUrl;
        return true;
      } else {
        throw new Error('No auth URL received from server');
      }
      
    } catch (error) {
      console.error('Error initiating HubSpot connection:', error);
      throw error;
    }
  },

  updateDeal: async (token, tenant, dealId, properties) => {
    try {
      const endpoint = `/v1/hubspot/deal/${dealId}`;
      
      const response = await axiosInstance.patch(endpoint, { properties }, {
        headers: {
          'Authorization': 'Bearer ' + token,
          'tenant': tenant
        }
      });
      
      console.log(`Deal ${dealId} updated successfully:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error updating deal ${dealId}:`, error);
      if (error.response?.status === 401) {
        return {
          status: 'error',
          code: 401,
          requires_reauth: true,
          message: 'HubSpot authentication required'
        };
      }
      throw error;
    }
  },

  updateDealStage: async (token, tenant, dealId, stageId) => {
  try {
    const endpoint = `/v1/hubspot/deals/${dealId}/stage`;
    
    const response = await axiosInstance.patch(endpoint, { stage_id: stageId }, {
      headers: {
        'Authorization': 'Bearer ' + token,
        'tenant': tenant
      }
    });
    
    console.log(`Deal ${dealId} stage updated successfully:`, response.data);
    return response.data;
  } catch (error) {
    console.error(`Error updating deal ${dealId} stage:`, error);
    if (error.response?.status === 401) {
      return {
        status: 'error',
        code: 401,
        requires_reauth: true,
        message: 'HubSpot authentication required'
      };
    }
    throw error;
  }
},

  getPipelines: async (token, tenant) => {
    try {
      const endpoint = '/v1/hubspot/pipelines';
      
      const response = await axiosInstance.get(endpoint, {
        headers: {
          'Authorization': 'Bearer ' + token,
          'tenant': tenant
        }
      });
      
      console.log('Retrieved pipelines:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching pipelines:', error);
      if (error.response?.status === 401) {
        return {
          status: 'error',
          code: 401,
          requires_reauth: true,
          message: 'HubSpot authentication required'
        };
      }
      throw error;
    }
  },

  getStageProbabilities: async (token, tenant) => {
    try {
      console.log('Fetching stage probabilities...');
      
      // Use the existing getPipelines method which now includes stage_probabilities
      const pipelinesData = await hubspotAPI.getPipelines(token, tenant);
      
      // Extract the stage probabilities from the pipelines response
      const stageProbabilities = pipelinesData.stage_probabilities || {};
      const stages = pipelinesData.stage_map || {};
      
      console.log(`Retrieved ${Object.keys(stageProbabilities).length} stage probabilities`);
      
      return {
        stage_probabilities: stageProbabilities,
        stages: stages,
        // Include full pipeline data in case it's needed
        pipelines: pipelinesData
      };
    } catch (error) {
      console.error('Error fetching stage probabilities:', error);
      if (error.response?.status === 401) {
        return {
          status: 'error',
          code: 401,
          requires_reauth: true,
          message: 'HubSpot authentication required',
          stage_probabilities: {},
          stages: {}
        };
      }
      // Return empty objects on error to prevent crashes
      return {
        stage_probabilities: {},
        stages: {}
      };
    }
  },

  getOwners: async (token, tenant) => {
    try {
      // Use the correct endpoint - this needs to be changed from 'owners' to 'settings/v3/owners'
      const endpoint = `/v1/hubspot/owners`;
      
      const response = await axiosInstance.get(endpoint, {
        headers: {
          'Authorization': 'Bearer ' + token,
          'tenant': tenant
        }
      });
      
      console.log('Raw owners response:', response.data);
      
      // Ensure the response has a standardized format even if the API returns different structures
      if (!response.data.results && Array.isArray(response.data)) {
        // If response.data is directly an array, wrap it
        return { results: response.data };
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching owners:', error);
      if (error.response?.status === 401) {
        return {
          status: 'error',
          code: 401,
          requires_reauth: true,
          message: 'HubSpot authentication required'
        };
      }
      // Return an empty results array on error to prevent UI crashes
      return { results: [] };
    }
  },

  getDeal: async (token, tenant, dealId) => {
    try {
      const endpoint = `/v1/hubspot/deal/${dealId}`;
      
      const response = await axiosInstance.get(endpoint, {
        headers: {
          'Authorization': 'Bearer ' + token,
          'tenant': tenant
        }
      });
      
      console.log(`Retrieved deal ${dealId}:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching deal ${dealId}:`, error);
      if (error.response?.status === 401) {
        return {
          status: 'error',
          code: 401,
          requires_reauth: true,
          message: 'HubSpot authentication required'
        };
      }
      throw error;
    }
  },

  refreshConnectionStatus: async (authorizationToken, tenant) => {
    try {
      // Get user_id from localStorage if available
      const userId = localStorage.getItem('hubspot_user_id');
      
      if (!userId) {
        console.warn('No hubspot_user_id found in localStorage for status refresh');
        return { connected: false };
      }
      
      console.log('Refreshing HubSpot connection status for user:', userId);
      const endpoint = `/v1/hubspot/status`;
      
      const response = await axiosInstance.get(endpoint, {
        headers: {
          'Authorization': 'Bearer ' + authorizationToken,
          'tenant': tenant
        }
      });
      
      console.log('Connection status refresh result:', response.data);
      
      // Update local storage based on server response
      if (response.data.connected) {
        localStorage.setItem('hubspot_connected', 'true');
      } else {
        localStorage.removeItem('hubspot_connected');
      }
      
      return response.data;
    } catch (error) {
      console.error('Error refreshing connection status:', error);
      return { connected: false, error: error.message };
    }
  },
  
  handleCallback: async (code, state) => {
    try {
      console.log('Processing OAuth callback:', {
        hasCode: !!code,
        hasState: !!state
      });
      
      const storedState = localStorage.getItem('hubspot_oauth_state');
      
      if (!storedState || storedState !== state) {
        console.error('State mismatch:', {
          stored: storedState,
          received: state
        });
        throw new Error('Security verification failed - state mismatch');
      }
      
      const response = await axiosInstance.post('/v1/hubspot/oauth-callback', {
        code,
        state
      });
      
      return {
        success: !!response.data?.success,
        userId: response.data?.user_id
      };
    } catch (error) {
      console.error('Error handling OAuth callback:', error);
      throw error;
    } finally {
      // Clear state regardless of outcome
      localStorage.removeItem('hubspot_oauth_state');
    }
  },
  
  linkUsers: async (tempUserId, realUserId) => {
    try {
      const response = await axiosInstance.post('/v1/hubspot/link-hubspot-user', {
        temp_user_id: tempUserId,
        real_user_id: realUserId
      });
      return response.data;
    } catch (error) {
      console.error('Error linking user IDs:', error);
      throw error;
    }
  }
};

// Quick Win API functions
export const quickWinAPI = {
  analyze: async (token, tenant) => {
    try {
      const endpoint = `/v1/quick-wins/analyze`;
      
      const response = await axiosInstance.post(endpoint, {}, {
        headers: {
          'Authorization': 'Bearer ' + token,
          'tenant': tenant
        }
      });
      
      console.log('Analysis started:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error starting analysis:', error);
      if (error.response?.status === 401) {
        return {
          status: 'error',
          code: 401,
          requires_reauth: true,
          message: 'HubSpot authentication required'
        };
      }
      throw error;
    }
  },

  getLatestResults: async (token, tenant) => {
    try {
      const endpoint = `/v1/quick-wins`;
      
      const response = await axiosInstance.get(endpoint, {
        headers: {
          'Authorization': 'Bearer ' + token,
          'tenant': tenant
        }
      });
      
      console.log('Retrieved latest results:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching latest results:', error);
      if (error.response?.status === 401) {
        return {
          status: 'error',
          code: 401,
          requires_reauth: true,
          message: 'HubSpot authentication required'
        };
      }
      throw error;
    }
  },

  getOpportunity: async (dealId, token, tenant, params = {}) => {
    try {
      const endpoint = `/v1/quick-wins/opportunity/${dealId}`;
      
      // Increase timeout for force_refresh request
      const requestConfig = {
        headers: {
          'Authorization': 'Bearer ' + token,
          'tenant': tenant
        },
        params
      };
      
      // Use longer timeout for comprehensive analysis
      if (params.force_refresh) {
        requestConfig.timeout = 300000; // 5 minutes for force_refresh
      }
      
      const response = await axiosInstance.get(endpoint, requestConfig);
      
      console.log(`Retrieved opportunity ${dealId}:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching opportunity ${dealId}:`, error);
      if (error.response?.status === 401) {
        return {
          status: 'error',
          code: 401,
          requires_reauth: true,
          message: 'HubSpot authentication required'
        };
      }
      throw error;
    }
  }
};

export const authenticatedFetch = async (endpoint, options = {}) => {
  try {
    const config = {
      method: options.method || 'GET',
      url: endpoint,
      headers: {
        ...options.headers
      },
      timeout: 900000, // Add 15 minute timeout
      timeoutErrorMessage: 'Request took too long to complete'
    };

    if (options.body) {
      config.data = options.body;
    }

    if (options.responseType) {
      config.responseType = options.responseType;
    }

    const response = await axiosInstance(config);
    return {
      ok: response.status >= 200 && response.status < 300,
      status: response.status,
      json: () => Promise.resolve(response.data),
      data: response.data
    };
  } catch (error) {
    if (error.message === 'Request took too long to complete') {
      throw new Error('Analysis is taking longer than expected. Please try again.');
    }
    throw error;
  }
};

// Helper function for retrying failed requests
export const retryRequest = async (requestFn, maxRetries = 3, delay = 1000) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await requestFn();
    } catch (error) {
      if (i === maxRetries - 1) throw error;
      await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
    }
  }
};

// Export the axios instance for direct use if needed
export const api = axiosInstance;
export { getApiUrl };
export default api;